import { Inject, Injectable } from '@angular/core';
import * as _ from 'lodash';
import { UtilitiesService } from '../../../../ng2/shared/services/utilities/utilities.service';
import { CreditRequirements } from '../../constants/credit-requirements.constant';
import { GapPlanStatuses, TValidGapPlanFrontendStatuses } from '../../constants/gap-plan-statuses.constant';
import { ValidCourseCodeRegex } from '../../constants/valid-course-code-regex.constant';
import { IGapPlan } from '../../typings/interfaces/gap-plan.interface';
import { DateHelpers } from './../../../../ng2/shared/services/date-helpers/date-helpers.service';

/* istanbul ignore next */
@Injectable()
export class ImGapPlan {
  constructor(@Inject('GapPlan') private GapPlan) {
    // empty block
  }

  getFrontendStatus(gapPlan: IGapPlan, currentTermYear: number): TValidGapPlanFrontendStatuses {
    // NOT included in net gap calc
    // DO NOT CHANGE ORDER

    if (this.isDeleted(gapPlan)) return GapPlanStatuses.frontend.REMOVED;

    if (this.isApplied(gapPlan)) return GapPlanStatuses.frontend.APPLIED;

    if (this.isActiveForPastTerm(gapPlan, currentTermYear)) return GapPlanStatuses.frontend.PAST;

    if (this.isActiveAndInvalid(gapPlan, currentTermYear)) return GapPlanStatuses.frontend.INVALID;
    // included in net gap calc

    if (this.isActiveForCurrentTerm(gapPlan, currentTermYear)) return GapPlanStatuses.frontend.PENDING;

    if (this.isActiveForFutureTerm(gapPlan, currentTermYear)) return GapPlanStatuses.frontend.FUTURE;

    return;
  }

  _isActive(gapPlan: IGapPlan): boolean {
    const backendStatus = gapPlan.status;

    return backendStatus === GapPlanStatuses.backend.ACTIVE;
  }

  _isInvalid(gapPlan: IGapPlan): boolean {
    const plan = gapPlan.plan;

    if (!plan) return true;
    let gapPlanMatcher = plan.split(' ')[0];
    gapPlanMatcher = gapPlanMatcher.split('-')[0];
    gapPlanMatcher = _.toUpper(gapPlanMatcher);
    const isValid = ValidCourseCodeRegex.test(gapPlanMatcher);

    return !isValid;
  }

  _isActiveAndValid(gapPlan: IGapPlan): boolean {
    const isActive = this._isActive(gapPlan);
    const isInvalid = this._isInvalid(gapPlan);

    return isActive && !isInvalid;
  }

  // REMOVED - these DONT get included in credit gap calculations
  isDeleted(gapPlan: IGapPlan): boolean {
    const backendStatus = gapPlan.status;

    return backendStatus === GapPlanStatuses.backend.DELETED;
  }

  // APPLIED - these DONT get included in credit gap calculations
  isApplied(gapPlan: IGapPlan): boolean {
    const backendStatus = gapPlan.status;

    return backendStatus === GapPlanStatuses.backend.APPLIED;
  }

  // INVALID - these DONT get included in credit gap calculations
  isActiveAndInvalid(gapPlan: IGapPlan, currentTermYear: number): boolean {
    const isActive = this._isActive(gapPlan);
    const isInvalid = this._isInvalid(gapPlan);

    return isActive && isInvalid;
  }

  // VALID PENDING - these DO get included in credit gap calculations
  isActiveForCurrentTerm(gapPlan: IGapPlan, currentTermYear: number): boolean {
    const termYear = gapPlan.termYear;
    const isActive = this._isActiveAndValid(gapPlan);
    const isCurrentTerm = termYear === currentTermYear;

    return isActive && isCurrentTerm;
  }

  // VALID FUTURE - these DO get included in credit gap calculations
  isActiveForFutureTerm(gapPlan: IGapPlan, currentTermYear: number): boolean {
    const termYear = gapPlan.termYear;
    const isActive = this._isActiveAndValid(gapPlan);
    const isFutureTerm = termYear > currentTermYear;

    return isActive && isFutureTerm;
  }

  // PAST - these DONT get included in credit gap calculations
  isActiveForPastTerm(gapPlan: IGapPlan, currentTermYear: number): boolean {
    const termYear = gapPlan.termYear;
    const isActive = this._isActive(gapPlan);
    const isPastTerm = termYear < currentTermYear;

    return isActive && isPastTerm;
  }

  getDisplayString(gapPlan: IGapPlan): string {
    if (gapPlan.termYear === 999) return 'Not a gap: ' + gapPlan.creditValue + 'cr';

    return gapPlan.plan + ': ' + gapPlan.creditValue + 'cr (' + gapPlan.termYear + ')';
  }

  isBeyondStudentGradDate(gapPlan: IGapPlan, effectiveGradDate: string, currentTermYear): boolean {
    const termYear = gapPlan.termYear; // 171, 172, 177, 181, 182, etc.
    const isActiveForFutureTerm = this.isActiveForFutureTerm(gapPlan, currentTermYear);

    if (!isActiveForFutureTerm) return false;

    if (termYear === 999) return false;
    const monthYearDate = UtilitiesService.prototype.getMonthYearDateFromTermYear(termYear);
    const gradDateMoment = DateHelpers.prototype.getMomentObjForMonthYearDate(effectiveGradDate);
    const monthYearMoment = DateHelpers.prototype.getMomentObjForMonthYearDate(monthYearDate);

    if (gradDateMoment.isBefore(monthYearMoment)) {
      return true;
    }

    return false;
  }

  filterForPending(_gapPlans, currentTermYear): IGapPlan[] {
    return _.reduce(
      _gapPlans,
      (result, _gapPlan: IGapPlan) => {
        const frontendStatus = this.getFrontendStatus(_gapPlan, currentTermYear);
        const isPending = frontendStatus === GapPlanStatuses.frontend.PENDING;

        if (isPending) result.push(_gapPlan);

        return result;
      },
      [],
    );
  }

  filterForFuture(_gapPlans, currentTermYear): IGapPlan[] {
    return _.reduce(
      _gapPlans,
      (result, _gapPlan: IGapPlan) => {
        const frontendStatus = this.getFrontendStatus(_gapPlan, currentTermYear);
        const isFuture = frontendStatus === GapPlanStatuses.frontend.FUTURE;

        if (isFuture) result.push(_gapPlan);

        return result;
      },
      [],
    );
  }

  filterForPendingAndFuture(_gapPlans, currentTermYear): IGapPlan[] {
    return _.reduce(
      _gapPlans,
      (result, _gapPlan: IGapPlan) => {
        const frontendStatus = this.getFrontendStatus(_gapPlan, currentTermYear);
        const isPendingOrFuture =
          frontendStatus === GapPlanStatuses.frontend.PENDING || frontendStatus === GapPlanStatuses.frontend.FUTURE;

        if (isPendingOrFuture) result.push(_gapPlan);

        return result;
      },
      [],
    );
  }

  filterForPendingFutureAndInvalid(_gapPlans, currentTermYear): IGapPlan[] {
    return _.reduce(
      _gapPlans,
      (result, _gapPlan: IGapPlan) => {
        const frontendStatus = this.getFrontendStatus(_gapPlan, currentTermYear);
        const isPendingFutureOrInvalid =
          frontendStatus === GapPlanStatuses.frontend.PENDING ||
          frontendStatus === GapPlanStatuses.frontend.FUTURE ||
          frontendStatus === GapPlanStatuses.frontend.INVALID;

        if (isPendingFutureOrInvalid) result.push(_gapPlan);

        return result;
      },
      [],
    );
  }

  filterForBeyondGradDate(_gapPlans, effectiveGradDate, currentTermYear): IGapPlan[] {
    return _.reduce(
      _gapPlans,
      (result, _gapPlan: IGapPlan) => {
        const isBeyond = this.isBeyondStudentGradDate(_gapPlan, effectiveGradDate, currentTermYear);

        if (isBeyond) result.push(_gapPlan);

        return result;
      },
      [],
    );
  }

  filterForGradReq(_gapPlans, gradReq: string, currentTermYear: number): IGapPlan[] {
    const creditRequirement = _.find(CreditRequirements, { camelCase: gradReq });
    const includedCreditRequirements = creditRequirement ? creditRequirement.includedCreditRequirements : null;
    const filtered = _.reduce(
      _gapPlans,
      (result, _gapPlan: IGapPlan) => {
        const frontendStatus = this.getFrontendStatus(_gapPlan, currentTermYear);
        const isPendingOrFuture =
          frontendStatus === GapPlanStatuses.frontend.PENDING || frontendStatus === GapPlanStatuses.frontend.FUTURE;

        if (isPendingOrFuture) {
          // CreditRequirements.TOTAL.camelCase is only exception

          if (gradReq === CreditRequirements.TOTAL.camelCase) {
            result.push(_gapPlan);
          } else {
            const gradReqIsIncluded = _.includes(includedCreditRequirements, _gapPlan.gradReq);

            if (gradReqIsIncluded) result.push(_gapPlan);
          }
        }

        return result;
      },
      [],
    );

    return filtered;
  }

  isIGapPlan(_gapPlan): _gapPlan is IGapPlan {
    return _gapPlan.singleUpdate !== undefined;
  }

  checkIsInstance(_gapPlan): IGapPlan {
    const isInstance = this.isIGapPlan(_gapPlan);

    if (!isInstance) return new this.GapPlan(_gapPlan);

    return _gapPlan;
  }

  createNew(studentId, schoolId, student) {
    // TODO: TMP - Remove studentId when there is more time (JC)
    let lastFirst = null;

    if (!studentId) {
      studentId = student._id;
      lastFirst = student.studentDetails.name.lastFirst;
    }

    const template = {
      _id: UtilitiesService.prototype.createV4Uuid(),
      version: '0.1.0',
      student: {
        lastFirst,
        studentId,
      },
      schoolId,
      gradReq: null,
      plan: null,
      termYear: null,
      creditValue: null,
      note: null,
    };

    return new this.GapPlan(template);
  }
}
